import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useGetIdentity } from '@refinedev/core';
import { useLocation, useNavigate } from 'react-router';
import { App } from 'antd';
import type { IERPUser } from '@admin-panel/types';
import { useGlobalContext } from '@admin-panel/hooks';
import { useMobileLayout } from '@admin-panel/components/lib/Layout/useMobileLayout';
import {
  ALL_FACILITIES,
  SAME_SITE_FACILITIES,
  isHubAlertRecipient,
  sameSite,
} from '../hubAlertRules';
import { useRingAll } from '../ringPreference';
import { SETUP_PATH, useSharedHubAlertSetup } from '../../hubAlertSetup/HubAlertSetupGate';
import {
  bindUser,
  clearNotice,
  closeDrawer,
  getAlertCenter,
  markWaitingSeen,
  noticeItems,
  refreshWaiting,
  showNotice,
  useAlertCenter,
  type AlertItem,
} from './alertCenterStore';
import {
  acknowledgeRinging,
  listenToOtherTabs,
  openedEverywhere,
  viewAlerts,
  viewTab,
  watchWorkingDay,
} from './alertCenterEffects';
import { isModalOpen, isTaskGuarded } from './alertFormat';
import { AlertCenterView, type RingScope, type SetupLine } from './AlertCenterView';
import { takeAlertSwitch, useAlertNavigation } from './useAlertNavigation';
import { isAlertDemoEnabled } from './devServer';
import type { FacilityBacklog } from '../hubBriefingRules';

// Local dev server only (removed from builds, absent in tests).
// Dev server only (never in a Stage/Production build), and even there only when asked for (`?alertDemo=1`).
const AlertDemoControls = isAlertDemoEnabled() ? lazy(() => import('./AlertDemoControls')) : null;

/** How long a calm notice (an alert for another facility) stays before only the bell has it. */
export const NOTICE_MS = 12_000;

/**
 * Where alerts ring loudly on this screen (drawer footer), for operators with several facilities.
 * The facility's name comes from today's alerts or waiting list when Hub has one.
 */
const ringScopeText = (
  selected: number | typeof ALL_FACILITIES | undefined,
  ringAll: boolean,
  facilityName: string | null,
) => {
  if (ringAll) return 'Alerts ring here for all your facilities.';
  if (selected === ALL_FACILITIES) {
    return 'All facilities selected: only urgent orders ring here. Others chime softly.';
  }
  const building =
    typeof selected === 'number' && SAME_SITE_FACILITIES.some(site => site.includes(selected))
      ? ' (and its building)'
      : '';
  return `Alerts ring here for ${facilityName ?? 'the facility selected in Hub'}${building} and for urgent orders. Your other facilities chime softly instead.`;
};

/** This device's alert setup, in the drawer footer (Check device / Set up now). */
const useSetupLine = (): SetupLine | null => {
  const setup = useSharedHubAlertSetup();
  if (!setup.applies || setup.mode === 'off' || !setup.settled) return null;
  if (setup.step !== null) {
    return {
      text: 'Order alerts are not set up on this device.',
      tone: 'error',
      action: 'Set up now',
    };
  }
  if (!setup.installed) {
    return {
      text: 'Alerts reach this device. Open Hub from its app icon to work.',
      tone: 'warning',
      action: 'Check device',
    };
  }
  return { text: 'Order alerts are on for this device.', tone: 'ok', action: 'Check device' };
};

/**
 * Hub's alert center for operators who get order alerts: the ringing alert card and the alerts
 * drawer (today's alerts and what is waiting), backed by alertCenterStore. Mounted once next to the
 * page area; HubAlerts feeds it from pushes and the briefing.
 */
const AlertCenter = () => {
  const { data: user } = useGetIdentity<IERPUser>();
  const recipient = isHubAlertRecipient(user?.roles);
  const center = useAlertCenter();
  const navigation = useAlertNavigation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMobile = useMobileLayout();
  const setup = useSetupLine();
  const { modal } = App.useApp();
  const { warehouseId } = useGlobalContext();
  const [ringAll, setRingAll] = useRingAll(recipient ? user?.id : null);
  const userFacilityIds = user?.adminUserAttributes?.warehouseIds;

  // A shared device: the next operator never sees the previous one's alerts. Unbound on logout too,
  // so signing in again restores the inbox from storage (nothing left ringing without sound).
  useEffect(() => {
    bindUser(recipient ? user?.id : null);
    return () => bindUser(null);
  }, [recipient, user?.id]);
  useEffect(() => (recipient ? listenToOtherTabs() : undefined), [recipient]);
  useEffect(() => (recipient ? watchWorkingDay() : undefined), [recipient]);

  // A calm notice shows for a while, then only the bell has its alerts.
  const noticeAt = center.notice?.at;
  useEffect(() => {
    if (noticeAt === undefined) return undefined;
    const timer = window.setTimeout(() => clearNotice(noticeAt), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [noticeAt]);

  // Switched to another facility: its alerts that did not ring (still unread) are announced.
  const previousSelection = useRef(warehouseId);
  useEffect(() => {
    const previous = previousSelection.current;
    previousSelection.current = warehouseId;
    // The first selection after load is not a switch.
    if (!recipient || previous === undefined || previous === warehouseId) return;
    // An earlier "here" no longer is.
    if (getAlertCenter().notice?.kind === 'here') clearNotice();
    // Opening an alert switched it: the operator is already on that alert's page.
    if (typeof warehouseId !== 'number' || takeAlertSwitch(warehouseId)) return;
    const keys = getAlertCenter()
      .items.filter(
        ({ alert, state }) =>
          state === 'new' && alert.facilityId !== null && sameSite(warehouseId, alert.facilityId),
      )
      .map(item => item.alert.key);
    showNotice('here', keys);
  }, [recipient, warehouseId]);

  // "2 min" in the open drawer keeps counting.
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!center.drawer.open) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [center.drawer.open]);

  if (!recipient) return null;

  // The calm "Waiting at …" bar: only on home, and only when work waits at the facility on screen (round 21: it
  // floated over work screens, at Dwarka named Brigade Orion, and View opened "Nothing waiting").
  const waitingHere =
    pathname === '/' &&
    !!center.waiting?.facilities.some(
      f =>
        (typeof warehouseId !== 'number' || f.facilityId === warehouseId) &&
        f.orders + f.pickups > 0,
    );
  const notice = center.notice ? { kind: center.notice.kind, items: noticeItems(center) } : null;
  const selectedName =
    typeof warehouseId === 'number'
      ? (center.items.find(item => item.alert.facilityId === warehouseId)?.alert.facilityName ??
        center.waiting?.facilities.find(facility => facility.facilityId === warehouseId)
          ?.facilityName ??
        null)
      : null;
  const ringScope: RingScope | null =
    (userFacilityIds?.length ?? 0) > 1 && warehouseId !== undefined
      ? { text: ringScopeText(warehouseId, ringAll, selectedName), ringAll }
      : null;

  /**
   * Leaving an open window (picking, QC, a form) loses what is not saved there: ask first. A
   * packing task asks by itself (only when it has unsaved scans or media), so it is not asked twice.
   */
  const leaveTaskThen = (go: () => void) => {
    if (!isModalOpen() || isTaskGuarded()) {
      go();
      return;
    }
    modal.confirm({
      title: 'Leave the open window?',
      content: 'Anything not yet saved there will be lost.',
      okText: 'Leave and open',
      cancelText: 'Stay',
      zIndex: 2200,
      onOk: go,
    });
  };

  // Marked opened (and the drawer closed) only once Hub went there: a refused leave keeps it.
  const onOpen = (item: AlertItem) =>
    leaveTaskThen(() => {
      if (navigation.openAlert(item.alert)) {
        openedEverywhere(item.alert.key);
        closeDrawer();
      }
    });

  const onStart = (backlog: FacilityBacklog, url: string) =>
    leaveTaskThen(() => {
      if (navigation.startWork(backlog, url)) closeDrawer();
    });

  return (
    <>
      {AlertDemoControls && (
        <Suspense fallback={null}>
          <AlertDemoControls />
        </Suspense>
      )}
      <AlertCenterView
        items={center.items}
        waiting={center.waiting}
        waitingUnseen={center.waitingUnseen && waitingHere}
        drawer={center.drawer}
        isMobile={isMobile}
        now={now}
        soundLocked={!center.soundReady}
        showFacility={navigation.showFacility}
        isElsewhere={navigation.isElsewhere}
        canSwitchTo={navigation.userCanSelect}
        canOpenPackages={navigation.canOpenPackages}
        setup={setup}
        notice={notice}
        ringScope={ringScope}
        onRingAll={setRingAll}
        onDismissNotice={() => clearNotice()}
        onView={tab => viewAlerts(tab)}
        onAcknowledgeAll={acknowledgeRinging}
        onOpen={onOpen}
        onStart={onStart}
        onRefreshWaiting={refreshWaiting}
        onDismissWaiting={markWaitingSeen}
        onTab={viewTab}
        onClose={closeDrawer}
        onSetup={() => {
          closeDrawer();
          navigate(SETUP_PATH);
        }}
      />
    </>
  );
};

export default AlertCenter;
